// ==================== Bond Candidate Filter ====================
//
// 化学键搜索的「元素对预过滤」。
//
// 背景：Schomaker–Stevenson 只是距离经验公式（predictedLength = r1+r2 - 0.09·|χ1-χ2|，
// cutoff = min(predictedLength·bondScale, MAX_BOND_LENGTH)），它只能判断「距离是否
// 合理」，无法判断「哪些元素对应当尝试成键」。对氧化物（如 LiAlSiO4）而言，
// Al-Li、Al-Si、O-O 等金属-金属 / 同种阴离子对会被宽 cutoff 误判成键。
//
// 本模块在 Schomaker–Stevenson 之前做元素对过滤：只对「应当尝试成键」的元素对
// 进入距离判定。过滤逻辑独立于 calculateBonds()，后者只需调用 shouldCheckBond()，
// 不直接知道哪些元素允许成键。
//
// 默认规则（氧化物/分子兼容，与 VESTA、OVITO 思路一致）：
//   - 允许：阳离子-阴离子（金属-O/S/F/Cl/Br/I/N 等），以及分子体系共价键（C-H/C-C/
//     C-N/C-O/N-H/S-H 等）；
//   - 禁止：金属-金属（含同种金属，如 Al-Li、Na-K、Fe-Mn、Li-Li），以及同种阴离子
//     （如 O-O），除非将来开启 Peroxide/Metal-Alloy 等 Preset。
//
// 扩展：未来 Bond Preset（Generic / Oxide / Sulfide / Organic / Metal Alloy / Custom）
// 可各自提供不同的 shouldCheckBond 实现，无需改动 calculateBonds() 主体。

/**
 * 金属元素集合（含主族金属、过渡金属、稀土、碱金属/碱土金属）。
 * 用于判定「金属-金属」对并禁止其成键。
 */
export const METALS = new Set<string>([
  // 碱金属 / 碱土金属
  'Li', 'Be', 'Na', 'Mg', 'K', 'Ca', 'Rb', 'Sr', 'Cs', 'Ba', 'Fr', 'Ra',
  // 过渡金属
  'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn',
  'Y', 'Zr', 'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd',
  'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg',
  // 镧系 / 锕系
  'La', 'Ce', 'Pr', 'Nd', 'Pm', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy', 'Ho', 'Er', 'Tm', 'Yb', 'Lu',
  'Ac', 'Th', 'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf',
  // 类金属/后过渡金属（常以阳离子形式存在于氧化物中，按金属处理以允许其与 O 成键）
  'Al', 'Ga', 'In', 'Sn', 'Tl', 'Pb', 'Bi',
]);

/**
 * 阴离子/类阴离子元素集合（成键时显负价的非金属）。
 * 金属-这类元素的对（阳离子-阴离子）允许成键；同种阴离子之间默认禁止（如 O-O）。
 */
export const ANIONS = new Set<string>([
  'O', 'S', 'Se', 'Te',  // 氧族
  'F', 'Cl', 'Br', 'I',  // 卤素
  'N',                   // 氮（氮化物）
]);

/** 该元素是否为金属（按 METALS 集合判定）。参数为纯元素符号（如 "Al"）。 */
export function isMetal(element: string): boolean {
  return METALS.has(element);
}

/** 该元素是否为阴离子（按 ANIONS 集合判定）。参数为纯元素符号（如 "O"）。 */
export function isAnion(element: string): boolean {
  return ANIONS.has(element);
}

/**
 * 分子体系允许的共价元素对（无论电负性高低）。
 * 用于保持有机/分子结构兼容：C-H、C-C、C-N、C-O、C-S、C-F/Cl/Br/I、
 * N-H、N-N、N-O、S-H、O-H、H-卤素 等。
 * 以「两端均为非金属且至少一端是 C/N/S/H」作为判据——这类对在分子中是真实共价键。
 */
function isMolecularCovalentPair(a: string, b: string): boolean {
  // 非金属（除金属外的元素）。H 视为非金属。
  if (isMetal(a) || isMetal(b)) return false;
  // 惰性气体不成键。
  const NOBLE = new Set(['He', 'Ne', 'Ar', 'Kr', 'Xe', 'Rn']);
  if (NOBLE.has(a) || NOBLE.has(b)) return false;
  // 至少一端是典型的成键非金属（C/N/S/H/O/卤素），允许共价。
  const COVALENT = new Set(['C', 'N', 'S', 'H', 'O', 'F', 'Cl', 'Br', 'I', 'P', 'B', 'Si']);
  return COVALENT.has(a) || COVALENT.has(b);
}

/**
 * Bond Candidate Filter：判断元素对是否应当进入 Schomaker–Stevenson 距离判定。
 *
 * 规则（默认氧化物/分子兼容 Preset）：
 *   1. 金属-金属（含同种金属）→ 禁止（避免 Al-Li、Na-K、Fe-Mn、Li-Li 等误判）。
 *   2. 同种阴离子之间（如 O-O、S-S）→ 禁止（除非将来开启 Peroxide）。
 *      注意：分子内的 O-O（如过氧化氢）属分子体系，由规则 3 放行；本规则只挡
 *      离子晶体中的 O-O。
 *   3. 分子共价对（C-H/C-C/C-N/...等，两端均非金属且含典型共价元素）→ 允许。
 *   4. 阳离子-阴离子（金属-O/S/N/卤素）→ 允许（氧化物/硫化物/氮化物/卤化物主键）。
 *   5. 其余（如金属-类金属非阴离子、惰性气体等）→ 禁止。
 *
 * 返回 true 才继续 Schomaker–Stevenson；false 跳过该对。
 */
export function shouldCheckBond(elementA: string, elementB: string): boolean {
  const mA = isMetal(elementA);
  const mB = isMetal(elementB);
  const aA = isAnion(elementA);
  const aB = isAnion(elementB);

  // 规则 1：金属-金属 → 禁止。
  const rule1 = mA && mB;
  if (rule1) {
    logDebug(elementA, elementB, mA, mB, aA, aB, 1, true, false);
    return false;
  }

  // 规则 2：同种阴离子（离子晶体中的 O-O 等）→ 禁止。
  const rule2 = elementA === elementB && aA;
  if (rule2) {
    logDebug(elementA, elementB, mA, mB, aA, aB, 2, true, false);
    return false;
  }

  // 规则 3：分子共价对（两端非金属 + 至少一端典型共价元素）→ 允许。
  const rule3 = isMolecularCovalentPair(elementA, elementB);
  if (rule3) {
    logDebug(elementA, elementB, mA, mB, aA, aB, 3, true, true);
    return true;
  }

  // 规则 4：阳离子（金属）-阴离子 → 允许。
  const rule4 = (mA && aB) || (mB && aA);
  if (rule4) {
    logDebug(elementA, elementB, mA, mB, aA, aB, 4, true, true);
    return true;
  }

  // 规则 5：其余默认禁止。
  logDebug(elementA, elementB, mA, mB, aA, aB, 5, false, false);
  return false;
}

// [DEBUG] 打印 shouldCheckBond 每一步：element、isMetal、isAnion、命中的规则、最终结果。
// 开启方式：浏览器控制台执行 window.__DEBUG_BONDS = true。
function logDebug(
  a: string, b: string,
  mA: boolean, mB: boolean, aA: boolean, aB: boolean,
  hitRule: number, ruleMatched: boolean, result: boolean
): void {
  if (typeof window === 'undefined' || !(window as unknown as { __DEBUG_BONDS?: boolean }).__DEBUG_BONDS) return;
  // eslint-disable-next-line no-console
  console.log(
    `[DEBUG shouldCheckBond] elementA="${a}" elementB="${b}" | isMetal(A)=${mA} isMetal(B)=${mB} | isAnion(A)=${aA} isAnion(B)=${aB} | Rule1=${mA && mB} Rule2=${a === b && aA} Rule3=${hitRule === 3} Rule4=${(mA && aB) || (mB && aA)} | HIT Rule${hitRule}(${ruleMatched ? 'matched' : 'no-match'}) | Result=${result}`
  );
}
